import type { CSSProperties } from 'react'

import { tokenVar } from '../../theme/tokenVar'

export type MarkerColorScheme = 'dark' | 'light'

/**
 * Map markers sit on satellite imagery, which does not change with the app's
 * theme, so white stays white here rather than resolving to a surface token.
 * It is the same contrast device as IncidentMapPin's white outline.
 */
const MAP_CONTRAST = '#FFFFFF'

const ALERT_SHADOW = '0 2px 7px 0 rgba(189, 25, 25, 0.5)'
const RESTING_SHADOW = '4px 0 14px 0 rgba(65, 71, 95, 0.17)'

// Ratios against the marker diameter, so a caller only passes `size`.
const FONT_RATIO = 0.28
const BADGE_RATIO = 0.48
const BADGE_FONT_RATIO = 0.24
const RING_INSET = 1.5
const RING_WIDTH = 2

const alertColorFor = (colorScheme: MarkerColorScheme) =>
  colorScheme === 'light'
    ? tokenVar('colors.light.status.error')
    : tokenVar('colors.status.error')

const restingRingColorFor = (colorScheme: MarkerColorScheme) =>
  colorScheme === 'light'
    ? tokenVar('colors.light.status.success')
    : tokenVar('colors.status.success')

// The resting circle is always MAP_CONTRAST white, so this label needs a dark
// value in both schemes — and the two schemes reach it through different token
// families. `text.on-accent` is darkBlue.900 in the dark scheme but #FFFFFF in
// the light one, which is why the light branch goes to `text.primary` instead.
// If the dark scheme's `on-accent` ever moves toward white, switch this branch
// to a `text.primary`-style token rather than letting the label disappear.
const restingTextColorFor = (colorScheme: MarkerColorScheme) =>
  colorScheme === 'light'
    ? tokenVar('colors.light.text.primary')
    : tokenVar('colors.text.on-accent')

interface StyleArgsIProps {
  size: number
  hasAlerts: boolean
  colorScheme: MarkerColorScheme
  isInteractive: boolean
}

export const circleStyles = ({
  size,
  hasAlerts,
  colorScheme,
  isInteractive,
}: StyleArgsIProps): CSSProperties => ({
  alignItems: 'center',
  background: hasAlerts ? alertColorFor(colorScheme) : MAP_CONTRAST,
  borderRadius: '50%',
  boxShadow: hasAlerts ? ALERT_SHADOW : RESTING_SHADOW,
  cursor: isInteractive ? 'pointer' : 'default',
  display: 'flex',
  height: `${size}px`,
  justifyContent: 'center',
  position: 'relative',
  width: `${size}px`,
})

/** Inset ring, drawn as a pseudo-free absolutely positioned overlay. */
export const ringStyles = ({
  size,
  hasAlerts,
  colorScheme,
}: Omit<StyleArgsIProps, 'isInteractive'>): CSSProperties => ({
  border: `${RING_WIDTH}px solid ${
    hasAlerts ? MAP_CONTRAST : restingRingColorFor(colorScheme)
  }`,
  borderRadius: '50%',
  height: `${size - RING_INSET * 2}px`,
  left: '50%',
  pointerEvents: 'none',
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: `${size - RING_INSET * 2}px`,
})

export const shortNameStyles = ({
  size,
  hasAlerts,
  colorScheme,
}: Omit<StyleArgsIProps, 'isInteractive'>): CSSProperties => ({
  color: hasAlerts ? MAP_CONTRAST : restingTextColorFor(colorScheme),
  fontSize: `${size * FONT_RATIO}px`,
  fontWeight: 600,
  letterSpacing: '-0.4px',
  lineHeight: 1,
})

export const badgeStyles = ({
  size,
  edge,
}: {
  size: number
  edge: 'left' | 'right'
}): CSSProperties => {
  const badgeSize = size * BADGE_RATIO
  const offset = -badgeSize / 4
  return {
    alignItems: 'center',
    background: MAP_CONTRAST,
    borderRadius: '50%',
    bottom: `${offset}px`,
    boxShadow: ALERT_SHADOW,
    display: 'flex',
    height: `${badgeSize}px`,
    justifyContent: 'center',
    position: 'absolute',
    width: `${badgeSize}px`,
    zIndex: 1,
    ...(edge === 'left' ? { left: `${offset}px` } : { right: `${offset}px` }),
  }
}

export const badgeTextStyles = ({
  size,
  colorScheme,
}: {
  size: number
  colorScheme: MarkerColorScheme
}): CSSProperties => ({
  color: alertColorFor(colorScheme),
  fontSize: `${size * BADGE_FONT_RATIO}px`,
  fontWeight: 700,
  letterSpacing: '-0.4px',
  lineHeight: 1,
})
